<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed, watch } from 'vue';
import { RoutePaths } from '../../Router/types/routePaths';
import { router } from '../../Router';
import { GuestService } from '@/api/guest/guestService';
import TableCanvas from '../../components/TableCanvas/TableCanvas.vue';
import { Guest, isGuestEligibleForTableAssignment } from '@/types/guest';
import { Table, TableShape, TableTab, TableWithGuests } from '../../types/table';
import LoadingSpinner from '@/components/Loading/LoadingSpinner.vue';
import { useToast } from 'primevue/usetoast';
import Toast from 'primevue/toast';
import MobileTablePlanner from '../../components/MobileTablePlanner/MobileTablePlanner.vue';
import { AdditionalQuestionsService } from '@/api/guest/additionalQuestionsService';
import { AdditionalQuestion } from '@/types/guest';
import { useGuestSync } from '@/composables/useGuestSync';

const toast = useToast();

interface CanvasDimensions {
  width: number;
  height: number;
  position: { x: number; y: number };
  scale: number;
  visibleCenter: { x: number; y: number };
}

const abortController = ref(new AbortController());
const isMobile = ref<boolean>(false);
const guests = ref<Guest[]>([]);
const declinedGuests = ref<Guest[]>([]);
const tables = ref<Table[]>([]);
const tableGuests = ref<Record<number, Guest[]>>({});
const selectedGuest = ref<Guest | null>(null);
const selectedTable = ref<Table | null>(null);
const dataLoading = ref(false);
const dataUnsaved = ref(false);
const selectedTableShape = ref<TableShape>(TableShape.ROUND);
const selectedTableLength = ref(4);
const selectedTableWidth = ref(2);
const isEditingTextInfo = ref(false);
const isEditingTableLabel = ref(false);
const editedTextInfo = ref('');
const editedTableLabel = ref('');
const showShapeDropdown = ref(false);
const activeTableTab = ref<TableTab>(TableTab.DETAILS);
const selectedSeatCount = ref(4);
const canvasDimensions = ref<CanvasDimensions>({
  width: 1000,
  height: 600,
  position: { x: 0, y: 0 },
  scale: 1,
  visibleCenter: { x: 500, y: 300 },
});
const isDragging = ref(false);
let animationFrameId: number | null = null;
const questionTemplates = ref<AdditionalQuestion[]>([]);

const currentTextInfo = computed(() => {
  return selectedGuest.value?.tableAdditionalInfo || '';
});

const columns = [
  { field: 'name', header: 'Imię' },
  { field: 'surname', header: 'Nazwisko' },
];

const unassignedGuestsSearchQuery = ref('');

const matchesGuestSearch = (guest: Guest, searchQuery: string): boolean => {
  if (!searchQuery) return true;

  const query = searchQuery.toLowerCase();
  const queryParts = query.match(/\S+\s*|\s+/g) || [];

  const matchPart = (part: string): boolean => {
    const exactMatch = part.endsWith(' ');
    const searchTerm = exactMatch ? part.trim() : part;

    if (exactMatch) {
      return guest.name.toLowerCase() === searchTerm || guest.surname.toLowerCase() === searchTerm;
    }
    return guest.name.toLowerCase().includes(searchTerm) || guest.surname.toLowerCase().includes(searchTerm);
  };

  if (queryParts.length > 1) {
    return queryParts.every(matchPart);
  }

  const exactMatch = query.endsWith(' ');
  const searchTerm = exactMatch ? query.trim() : query;

  if (exactMatch) {
    return guest.name.toLowerCase() === searchTerm || guest.surname.toLowerCase() === searchTerm;
  }
  return guest.name.toLowerCase().includes(searchTerm) || guest.surname.toLowerCase().includes(searchTerm);
};

const filteredUnassignedGuests = computed(() => {
  if (!unassignedGuestsSearchQuery.value) return guests.value;
  return guests.value.filter((guest) => matchesGuestSearch(guest, unassignedGuestsSearchQuery.value));
});

const clearGuestTableAssignment = (guest: Guest): Guest => ({
  ...guest,
  tableNumber: -1,
  seatIndex: undefined,
  tableLayoutInfo: undefined,
});

const partitionGuestsForTablePlanner = (allGuests: Guest[]): void => {
  const eligibleGuests: Guest[] = [];
  const ineligibleGuests: Guest[] = [];

  allGuests.forEach((guest) => {
    if (isGuestEligibleForTableAssignment(guest)) {
      eligibleGuests.push(guest);
      return;
    }

    ineligibleGuests.push(clearGuestTableAssignment(guest));
  });

  declinedGuests.value = ineligibleGuests;
  guests.value = eligibleGuests;
};

const getGuestsForSave = (): Guest[] => {
  const tableGuestsList = Object.entries(tableGuests.value).flatMap(([tableIndex, tableGuestItems]) => {
    const table = tables.value[Number(tableIndex)];
    if (!table) return [];

    return tableGuestItems.map((guest) => ({
      ...guest,
      tableNumber: Number(tableIndex) + 1,
      tableLayoutInfo: {
        shape: table.shape,
        position: table.position,
        label: table.label,
        length: table.length,
        width: table.width,
        seatCount: table.seatCount,
        number: table.number,
      },
      seatIndex: guest.seatIndex,
    }));
  });

  return [...tableGuestsList, ...guests.value, ...declinedGuests.value];
};

const selectedSeatIndex = ref<number | null>(null);

const handleShowUnassignedGuests = (seatIndex: number) => {
  selectedSeatIndex.value = seatIndex;
  activeTableTab.value = TableTab.GUESTS;
};

const onGuestSelected = (guest: Guest): void => {
  if (selectedSeatIndex.value !== null && selectedTable.value) {
    const tableIndex = tables.value.indexOf(selectedTable.value);
    selectedGuest.value = guest;
    addGuestToTable(tableIndex, selectedSeatIndex.value);
    selectedSeatIndex.value = null;
  } else {
    selectedGuest.value = guest;
    selectedTable.value = null;
    editedTextInfo.value = guest.tableAdditionalInfo || '';
  }
};

const removeFromTable = (): void => {
  if (!selectedGuest.value) return;

  const guest = selectedGuest.value;
  const tableIndex = guest.tableNumber ? guest.tableNumber - 1 : -1;

  if (tableIndex >= 0 && tableGuests.value[tableIndex]) {
    tableGuests.value[tableIndex] = tableGuests.value[tableIndex].filter((g) => g.id !== guest.id);

    guest.tableNumber = -1;
    guest.seatIndex = undefined;
    guests.value.push(guest);

    dataUnsaved.value = true;
    selectedGuest.value = null;
  }
};

const isAnyGuestSelected = computed((): boolean => {
  return selectedGuest.value !== null;
});

const isGuestSelected = (guest: Guest): boolean => {
  return selectedGuest.value?.id === guest.id;
};

const onCancelAdding = (): void => {
  selectedGuest.value = null;
};

const addTable = (): void => {
  const seatCount =
    selectedTableShape.value === TableShape.ROUND
      ? selectedSeatCount.value
      : selectedTableLength.value * selectedTableWidth.value;

  const centerX = canvasDimensions.value.visibleCenter.x;
  const centerY = canvasDimensions.value.visibleCenter.y;

  const tableIndex = tables.value.length;
  tables.value.push({
    shape: selectedTableShape.value,
    position: { x: centerX, y: centerY },
    seatCount,
    number: tableIndex + 1,
    length: selectedTableShape.value === TableShape.RECTANGULAR ? selectedTableLength.value : 0,
    width: selectedTableShape.value === TableShape.RECTANGULAR ? selectedTableWidth.value : 0,
  });
  tableGuests.value[tableIndex] = [];
  dataUnsaved.value = true;
  showShapeDropdown.value = false;
};

const selectTableShape = (shape: TableShape): void => {
  selectedTableShape.value = shape;
};

const handleTablePositionChange = (index: number, position: { x: number; y: number }): void => {
  if (tables.value[index]) {
    tables.value[index].position = position;
    dataUnsaved.value = true;
  }
};

const handleSeatCountChange = (tableIndex: number, newCount: number, newLength?: number, newWidth?: number): void => {
  const table = tables.value[tableIndex];
  const tableGuestsList = tableGuests.value[tableIndex] || [];

  if (!table) return;

  let newSeatCount = newCount;

  if (table.shape === TableShape.RECTANGULAR) {
    const length = newLength ?? table.length ?? 4;
    const width = newWidth ?? table.width ?? 2;
    newSeatCount = length * width;
  }

  if (newSeatCount < tableGuestsList.length) {
    alert(
      `Nie można zmniejszyć liczby miejsc do ${newSeatCount}, ponieważ przy stole jest ${tableGuestsList.length} gości. Najpierw usuń gości ze stołu.`,
    );
    return;
  }

  const updatedTable = {
    ...table,
    seatCount: newSeatCount,
  };

  if (table.shape === TableShape.RECTANGULAR) {
    if (newLength !== undefined) {
      updatedTable.length = newLength;
    }
    if (newWidth !== undefined) {
      updatedTable.width = newWidth;
    }
  }

  if (table.shape === TableShape.ROUND && newCount < (table.seatCount ?? 0)) {
    const guestsToRelocate = tableGuestsList.filter(
      (guest) => guest.seatIndex !== undefined && guest.seatIndex >= newCount,
    );

    guestsToRelocate.forEach((guest) => {
      const availableSeat = Array.from({ length: newCount }, (_, i) => i).find(
        (seatIndex) => !tableGuestsList.some((g) => g.seatIndex === seatIndex),
      );

      if (availableSeat !== undefined) {
        guest.seatIndex = availableSeat;
      } else {
        tableGuests.value[tableIndex] = tableGuestsList.filter((g) => g.id !== guest.id);
        guest.tableNumber = -1;
        guest.seatIndex = undefined;
        guests.value.push(guest);
      }
    });
  }

  tables.value[tableIndex] = updatedTable;
  dataUnsaved.value = true;

  if (selectedTable.value && selectedTable.value === table) {
    selectedTable.value = updatedTable;
  }
};

const onTablesReturn = (e: Event): void => {
  if (dataUnsaved.value) {
    e.preventDefault();
    if (confirm('Masz niezapisane zmiany. Czy na pewno chcesz wrócić?')) {
      router.push(RoutePaths.guests);
    }
  }
};

const DRAFT_KEY = 'guest_tables_draft';
const DRAFT_TIMESTAMP_KEY = 'guest_tables_draft_timestamp';
const DRAFT_MAX_SIZE = 5 * 1024 * 1024; // 5MB limit
const DRAFT_SAVE_DELAY = 1000; // 1 second debounce

let saveDraftTimeout: number | null = null;

const saveDraft = (): void => {
  try {
    if (!window.localStorage) {
      console.warn('localStorage is not available');
      return;
    }

    const draftData = {
      tables: tables.value,
      guests: guests.value,
      declinedGuests: declinedGuests.value,
      tableGuests: tableGuests.value,
      timestamp: new Date().toISOString(),
    };

    const serializedData = JSON.stringify(draftData);

    if (serializedData.length > DRAFT_MAX_SIZE) {
      console.warn('Draft data exceeds size limit');
      return;
    }

    localStorage.setItem(DRAFT_KEY, serializedData);
    localStorage.setItem(DRAFT_TIMESTAMP_KEY, draftData.timestamp);
  } catch (error) {
    console.error('Failed to save draft:', error);
  }
};

const loadDraft = (): void => {
  try {
    if (!window.localStorage) {
      console.warn('localStorage is not available');
      return;
    }

    const draftData = localStorage.getItem(DRAFT_KEY);
    if (!draftData) return;

    const parsedData = JSON.parse(draftData);

    if (!Array.isArray(parsedData.tables) || !Array.isArray(parsedData.guests)) {
      console.error('Invalid draft data structure');
      clearDraft();
      return;
    }

    tables.value = parsedData.tables;
    guests.value = parsedData.guests;
    declinedGuests.value = Array.isArray(parsedData.declinedGuests) ? parsedData.declinedGuests : [];
    tableGuests.value = parsedData.tableGuests;

    const ineligibleUnassignedGuests = guests.value.filter((guest) => !isGuestEligibleForTableAssignment(guest));
    guests.value = guests.value.filter(isGuestEligibleForTableAssignment);
    declinedGuests.value.push(...ineligibleUnassignedGuests.map(clearGuestTableAssignment));

    Object.entries(tableGuests.value).forEach(([tableIndex, tableList]) => {
      const ineligibleTableGuests = tableList.filter((guest) => !isGuestEligibleForTableAssignment(guest));
      if (!ineligibleTableGuests.length) {
        return;
      }

      tableGuests.value[Number(tableIndex)] = tableList.filter(isGuestEligibleForTableAssignment);
      declinedGuests.value.push(...ineligibleTableGuests.map(clearGuestTableAssignment));
    });

    dataUnsaved.value = true;
  } catch (error) {
    console.error('Failed to load draft:', error);
    clearDraft();
  }
};

const clearDraft = (): void => {
  try {
    if (!window.localStorage) return;
    localStorage.removeItem(DRAFT_KEY);
    localStorage.removeItem(DRAFT_TIMESTAMP_KEY);
  } catch (error) {
    console.error('Failed to clear draft:', error);
  }
};

const hasDraft = computed((): boolean => {
  try {
    return !!localStorage.getItem(DRAFT_KEY);
  } catch {
    return false;
  }
});

const getDraftTimestamp = computed((): string | null => {
  try {
    return localStorage.getItem(DRAFT_TIMESTAMP_KEY);
  } catch {
    return null;
  }
});

const onTablesSave = async (): Promise<void> => {
  try {
    dataLoading.value = true;

    const allGuests = getGuestsForSave();

    await GuestService.saveGuestChanges({
      abortSignal: abortController.value.signal,
      localGuests: allGuests.map((guest) => GuestService.guestToApiPayload(guest)),
    });

    clearDraft();
    toast.add({
      severity: 'success',
      summary: 'Sukces',
      detail: 'Pomyślnie zapisano układ stołów. Możesz teraz wrócić do listy gości.',
      life: 5000,
    });
    dataUnsaved.value = false;
  } catch (error) {
    console.error('Failed to save tables:', error);
    toast.add({
      severity: 'error',
      summary: 'Błąd',
      detail: 'Nie udało się zapisać zmian. Spróbuj ponownie później.',
      life: 3000,
    });
  } finally {
    dataLoading.value = false;
  }
};

watch(
  [tables, guests, declinedGuests, tableGuests],
  () => {
    if (dataUnsaved.value) {
      if (saveDraftTimeout) {
        clearTimeout(saveDraftTimeout);
      }
      saveDraftTimeout = window.setTimeout(() => {
        saveDraft();
        saveDraftTimeout = null;
      }, DRAFT_SAVE_DELAY);
    }
  },
  { deep: true },
);

const fillTables = (): void => {
  tables.value = [];
  tableGuests.value = {};

  const tableNumbers = new Set<number>(
    guests.value.map((g) => g.tableNumber).filter((num): num is number => num !== undefined && num > 0),
  );

  tableNumbers.forEach((tableNumber) => {
    const tableGuestsList = guests.value.filter(
      (g) => g.tableNumber === tableNumber && isGuestEligibleForTableAssignment(g),
    );
    const firstGuest = tableGuestsList[0];
    const tableLayoutInfo = firstGuest?.tableLayoutInfo;

    const guestsWithSeats = tableGuestsList.map((guest, index) => ({
      ...guest,
      seatIndex: guest.seatIndex ?? index,
    }));

    const tableShape = tableLayoutInfo?.shape === TableShape.ROUND ? TableShape.ROUND : TableShape.RECTANGULAR;
    const defaultPosition = { x: 100 + (tableNumber - 1) * 350, y: 100 };

    let seatCount = 4;
    if (tableShape === TableShape.ROUND) {
      seatCount = tableLayoutInfo?.seatCount ?? 4;
    } else {
      const length = typeof tableLayoutInfo?.length === 'number' ? tableLayoutInfo.length : 4;
      const width = typeof tableLayoutInfo?.width === 'number' ? tableLayoutInfo.width : 2;
      seatCount = length * width;
    }

    const tableIndex = tables.value.length;
    tables.value.push({
      shape: tableShape,
      position: tableLayoutInfo?.position || defaultPosition,
      seatCount: seatCount,
      label: tableLayoutInfo?.label || '',
      number: tableNumber,
      length: tableShape === TableShape.RECTANGULAR ? tableLayoutInfo?.length || 4 : 0,
      width: tableShape === TableShape.RECTANGULAR ? tableLayoutInfo?.width || 2 : 0,
    });
    tableGuests.value[tableIndex] = guestsWithSeats;
  });

  guests.value = guests.value.filter(
    (g) => (!g.tableNumber || g.tableNumber <= 0) && isGuestEligibleForTableAssignment(g),
  );
};

const addGuestToTable = (tableIndex: number, seatIndex: number): void => {
  if (!selectedGuest.value || !isGuestEligibleForTableAssignment(selectedGuest.value)) return;

  const guest = selectedGuest.value;
  const table = tables.value[tableIndex];
  const tableGuestsList = tableGuests.value[tableIndex] || [];

  if (table) {
    const currentGuest = tableGuestsList.find((g) => g.seatIndex === seatIndex);
    const isSameTable = guest.tableNumber === tableIndex + 1;

    if (currentGuest && currentGuest.id !== guest.id) {
      currentGuest.tableNumber = -1;
      currentGuest.seatIndex = undefined;
      guests.value.push(currentGuest);
    }

    if (isSameTable) {
      tableGuests.value[tableIndex] = tableGuestsList.filter((g) => g.id !== guest.id);
    } else {
      Object.keys(tableGuests.value).forEach((index) => {
        const tableGuestsList = tableGuests.value[Number(index)];
        if (tableGuestsList) {
          tableGuests.value[Number(index)] = tableGuestsList.filter((g) => g.id !== guest.id);
        }
      });
      guests.value = guests.value.filter((g) => g.id !== guest.id);
    }

    guest.tableNumber = tableIndex + 1;
    guest.seatIndex = seatIndex;

    const updatedTableGuests = tableGuests.value[tableIndex] || [];
    tableGuests.value[tableIndex] = [...updatedTableGuests, guest];

    dataUnsaved.value = true;
    selectedTable.value = table;
    activeTableTab.value = TableTab.DETAILS;
    selectedGuest.value = null;
  }
};

const onEditTextInfo = (): void => {
  isEditingTextInfo.value = true;
};

const onSaveTextInfo = (): void => {
  if (selectedGuest.value) {
    selectedGuest.value.tableAdditionalInfo = editedTextInfo.value;
    dataUnsaved.value = true;
    isEditingTextInfo.value = false;
  }
};

const onCancelTextInfoEdit = (): void => {
  isEditingTextInfo.value = false;
  editedTextInfo.value = selectedGuest.value?.tableAdditionalInfo || '';
};

const onTableSelected = (table: Table): void => {
  const tableIndex = tables.value.findIndex(
    (t) =>
      Math.abs(t.position.x - table.position.x) < 0.1 &&
      Math.abs(t.position.y - table.position.y) < 0.1 &&
      t.shape === table.shape,
  );

  if (tableIndex !== -1) {
    const foundTable = tables.value[tableIndex];
    if (foundTable) {
      selectedTable.value = foundTable;
      selectedGuest.value = null;
      editedTableLabel.value = foundTable.label || '';
      activeTableTab.value = TableTab.DETAILS;
    }
  }
};

const onCancelTableEdit = (): void => {
  selectedTable.value = null;
};

const onEditTableLabel = (): void => {
  isEditingTableLabel.value = true;
};

const onSaveTableLabel = (): void => {
  if (selectedTable.value) {
    selectedTable.value.label = editedTableLabel.value;
    dataUnsaved.value = true;
    isEditingTableLabel.value = false;
  }
};

const onCancelTableLabelEdit = (): void => {
  isEditingTableLabel.value = false;
  editedTableLabel.value = selectedTable.value?.label || '';
};

const deleteTable = (tableIndex: number): void => {
  const table = tables.value[tableIndex];
  if (!table) return;

  if (confirm(`Czy na pewno chcesz usunąć stół ${table.label ? `"${table.label}"` : `nr ${tableIndex + 1}`}?`)) {
    const guestsToMove = (tableGuests.value[tableIndex] || []).map((guest) => ({
      ...guest,
      tableNumber: -1,
      seatIndex: undefined,
    }));
    guests.value.push(...guestsToMove);

    const tempTableGuests = { ...tableGuests.value };
    delete tempTableGuests[tableIndex];

    tables.value.splice(tableIndex, 1);

    const newTableGuests: Record<number, Guest[]> = {};
    Object.entries(tempTableGuests).forEach(([oldIndex, guests]) => {
      const newIndex = Number(oldIndex) > tableIndex ? Number(oldIndex) - 1 : Number(oldIndex);
      newTableGuests[newIndex] = guests.map((guest) => ({
        ...guest,
        tableNumber: newIndex + 1,
      }));
    });

    tables.value.forEach((table, index) => {
      table.number = index + 1;
    });

    tableGuests.value = newTableGuests;

    dataUnsaved.value = true;
    selectedTable.value = null;
  }
};

const isTableLayoutInfo = (data: unknown): data is Table => {
  if (!data || typeof data !== 'object') return false;
  const layout = data as Record<string, unknown>;
  return (
    (layout['shape'] === TableShape.ROUND || layout['shape'] === TableShape.RECTANGULAR) &&
    typeof layout['position'] === 'object' &&
    layout['position'] !== null &&
    typeof (layout['position'] as { x: unknown })['x'] === 'number' &&
    typeof (layout['position'] as { y: unknown })['y'] === 'number' &&
    (layout['label'] === undefined || typeof layout['label'] === 'string') &&
    (layout['length'] === undefined || typeof layout['length'] === 'number') &&
    (layout['width'] === undefined || typeof layout['width'] === 'number') &&
    (layout['seatCount'] === undefined || typeof layout['seatCount'] === 'number') &&
    (layout['number'] === undefined || typeof layout['number'] === 'number')
  );
};

const switchTableTab = (tab: TableTab): void => {
  if (
    selectedTable.value &&
    tab === TableTab.GUESTS &&
    (tableGuests.value[tables.value.indexOf(selectedTable.value)]?.length || 0) >=
      (selectedTable.value.seatCount || 0) &&
    guests.value.length === 0
  ) {
    return;
  }
  activeTableTab.value = tab;
};

const throttle = <T extends unknown[]>(fn: (...args: T) => void) => {
  return (...args: T) => {
    if (animationFrameId !== null) {
      cancelAnimationFrame(animationFrameId);
    }
    animationFrameId = requestAnimationFrame(() => {
      fn(...args);
      animationFrameId = null;
    });
  };
};

const handleCanvasResize = throttle((dimensions: CanvasDimensions) => {
  if (isDragging.value) {
    canvasDimensions.value = {
      ...canvasDimensions.value,
      position: dimensions.position,
      scale: dimensions.scale,
    };
  } else {
    canvasDimensions.value = { ...dimensions };
  }
});

const checkMobileDevice = () => {
  const isSmallScreen = window.matchMedia('(max-width: 768px)').matches;

  const touchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

  const hasMouse = window.matchMedia('(pointer: fine)').matches;

  isMobile.value = isSmallScreen || (touchDevice && !hasMouse);
};

const loadGuestsFromServer = async (): Promise<{ updatedAt: string }> => {
  const guestsData = await GuestService.getGuestsData({
    abortSignal: abortController.value.signal,
  });

  questionTemplates.value = AdditionalQuestionsService.processAndUpdateTemplates(
    guestsData.data as Parameters<typeof AdditionalQuestionsService.processAndUpdateTemplates>[0],
  );

  guests.value = guestsData.data.map((guest) => {
    const tableLayoutInfo = guest.tableLayoutInfo;
    const parsedTableLayoutInfo =
      tableLayoutInfo && isTableLayoutInfo(tableLayoutInfo)
        ? {
            shape: tableLayoutInfo['shape'] === 'round' ? TableShape.ROUND : TableShape.RECTANGULAR,
            position: tableLayoutInfo['position'],
            number: tableLayoutInfo['number'] ? Number(tableLayoutInfo['number']) : -1,
            seatCount: tableLayoutInfo['seatCount'] ? Number(tableLayoutInfo['seatCount']) : -1,
            ...(tableLayoutInfo['label'] !== undefined ? { label: tableLayoutInfo['label'] } : {}),
            ...(tableLayoutInfo['length'] !== undefined ? { length: Number(tableLayoutInfo['length']) } : {}),
            ...(tableLayoutInfo['width'] !== undefined ? { width: Number(tableLayoutInfo['width']) } : {}),
          }
        : undefined;

    const normalizedQuestions = AdditionalQuestionsService.normalizeGuestQuestions(
      guest as Parameters<typeof AdditionalQuestionsService.normalizeGuestQuestions>[0],
      questionTemplates.value,
    );

    const parsedGuest: Guest = {
      id: String(guest.id),
      name: String(guest.name),
      surname: String(guest.surname),
      presence: guest.presence === undefined ? undefined : Boolean(guest.presence),
      connection: String(guest.connection),
      connectedGuestId: String(guest.connectedGuestId),
      tableNumber: guest.tableNumber ? Number(guest.tableNumber) : -1,
      tableLayoutInfo: parsedTableLayoutInfo,
      tableAdditionalInfo: String(guest.tableAdditionalInfo || ''),
      additionalQuestions: normalizedQuestions,
      seatIndex: guest.seatIndex !== undefined ? Number(guest.seatIndex) : undefined,
    };
    return parsedGuest;
  });

  partitionGuestsForTablePlanner(guests.value);
  fillTables();
  dataUnsaved.value = false;

  return { updatedAt: guestsData.updatedAt };
};

useGuestSync({
  onExternalUpdate: async () => {
    if (dataUnsaved.value) {
      const shouldReload = confirm(
        'Lista gości została zaktualizowana w innej karcie. Masz niezapisane zmiany stołów — wczytać dane z serwera?',
      );

      if (!shouldReload) {
        return;
      }
    }

    try {
      dataLoading.value = true;
      clearDraft();
      await loadGuestsFromServer();
      toast.add({
        severity: 'info',
        summary: 'Lista zaktualizowana',
        detail: 'Dane gości zostały odświeżone z serwera.',
        life: 3000,
      });
    } catch (error) {
      console.error('Failed to reload guests after sync:', error);
    } finally {
      dataLoading.value = false;
    }
  },
});

onMounted(async () => {
  checkMobileDevice();
  window.addEventListener('resize', checkMobileDevice);
  dataLoading.value = true;

  try {
    const { updatedAt } = await loadGuestsFromServer();

    if (hasDraft.value) {
      const serverUpdatedAt = new Date(updatedAt).getTime();
      const draftUpdatedAt = new Date(getDraftTimestamp.value || '').getTime();
      const draftTimestamp = new Date(getDraftTimestamp.value || '').toLocaleString();

      if (Number.isFinite(draftUpdatedAt) && draftUpdatedAt < serverUpdatedAt) {
        clearDraft();
        toast.add({
          severity: 'info',
          summary: 'Szkic pominięty',
          detail: 'Na serwerze jest nowsza wersja listy gości. Nieaktualny szkic został usunięty.',
          life: 5000,
        });
      } else if (confirm(`Znaleziono niezapisany szkic z ${draftTimestamp}. Czy chcesz go wczytać?`)) {
        loadDraft();
      } else {
        clearDraft();
      }
    }
  } catch (error) {
    console.error('Failed to load guests data:', error);
    toast.add({
      severity: 'error',
      summary: 'Błąd',
      detail: 'Nie udało się załadować danych gości. Spróbuj ponownie później.',
      life: 3000,
    });
  } finally {
    dataLoading.value = false;
  }
});

onUnmounted(() => {
  if (saveDraftTimeout) {
    clearTimeout(saveDraftTimeout);
  }
  if (animationFrameId !== null) {
    cancelAnimationFrame(animationFrameId);
  }
  if (abortController.value) {
    abortController.value.abort('Page changed...');
  }
  window.removeEventListener('resize', checkMobileDevice);
});
</script>

<template>
  <div class="guest-tables">
    <MobileTablePlanner v-if="isMobile" />
    <template v-else>
      <LoadingSpinner
        v-if="dataLoading"
        :background="'rgba(0, 0, 0, 0.2)'"
      />
      <div class="guest-tables__header">
        <div class="guest-tables__navigation">
          <router-link
            :to="dataUnsaved ? '' : RoutePaths.guests"
            class="guest-tables__link"
          >
            <button
              class="guest-tables__button guest-tables__button--info"
              @click="onTablesReturn"
            >
              <i class="pi pi-arrow-left"></i>
              <span class="guest-tables__button-text">Powrót do edycji gości</span>
            </button>
          </router-link>
          <transition
            name="fade"
            mode="out-in"
          >
            <button
              v-if="isAnyGuestSelected"
              class="guest-tables__button guest-tables__button--danger"
              @click="onCancelAdding"
            >
              <i class="pi pi-times"></i>
              <span class="guest-tables__button-text">Anuluj edycję</span>
            </button>
          </transition>
        </div>
        <div class="guest-tables__actions">
          <div class="guest-tables__shape-dropdown">
            <button
              class="guest-tables__button guest-tables__button--primary"
              @click="showShapeDropdown = !showShapeDropdown"
            >
              <i class="pi pi-plus"></i>
              <span class="guest-tables__button-text">Dodaj stół</span>
            </button>
            <transition name="fade">
              <div
                v-if="showShapeDropdown"
                class="guest-tables__dropdown-menu"
              >
                <div class="guest-tables__dropdown-section">
                  <h4 class="guest-tables__dropdown-title">Wybierz kształt stołu</h4>
                  <div class="guest-tables__dropdown-options">
                    <button
                      class="guest-tables__dropdown-item"
                      :class="{ 'guest-tables__dropdown-item--selected': selectedTableShape === TableShape.ROUND }"
                      @click="selectTableShape(TableShape.ROUND)"
                    >
                      <i class="pi pi-circle"></i>
                      Okrągły
                    </button>
                    <button
                      class="guest-tables__dropdown-item"
                      :class="{
                        'guest-tables__dropdown-item--selected': selectedTableShape === TableShape.RECTANGULAR,
                      }"
                      @click="selectTableShape(TableShape.RECTANGULAR)"
                    >
                      <i class="pi pi-stop"></i>
                      Prostokątny
                    </button>
                  </div>
                </div>
                <div class="guest-tables__dropdown-section">
                  <h4 class="guest-tables__dropdown-title">
                    {{ selectedTableShape === TableShape.ROUND ? 'Liczba miejsc' : 'Wymiary stołu' }}
                  </h4>
                  <template v-if="selectedTableShape === TableShape.ROUND">
                    <div class="guest-tables__seat-count">
                      <button
                        class="guest-tables__seat-count-button"
                        @click="selectedSeatCount = Math.max(2, selectedSeatCount - 1)"
                      >
                        <i class="pi pi-minus"></i>
                      </button>
                      <span class="guest-tables__seat-count-value">{{ selectedSeatCount }}</span>
                      <button
                        class="guest-tables__seat-count-button"
                        @click="selectedSeatCount = Math.min(100, selectedSeatCount + 1)"
                      >
                        <i class="pi pi-plus"></i>
                      </button>
                    </div>
                  </template>
                  <template v-else>
                    <div class="guest-tables__dimensions">
                      <div class="guest-tables__dimension">
                        <span class="guest-tables__dimension-label">Długość:</span>
                        <div class="guest-tables__seat-count">
                          <button
                            class="guest-tables__seat-count-button"
                            @click="selectedTableLength = Math.max(2, selectedTableLength - 1)"
                          >
                            <i class="pi pi-minus"></i>
                          </button>
                          <span class="guest-tables__seat-count-value">{{ selectedTableLength }}</span>
                          <button
                            class="guest-tables__seat-count-button"
                            @click="selectedTableLength = Math.min(100, selectedTableLength + 1)"
                          >
                            <i class="pi pi-plus"></i>
                          </button>
                        </div>
                      </div>
                      <div class="guest-tables__dimension">
                        <span class="guest-tables__dimension-label">Szerokość:</span>
                        <div class="guest-tables__seat-count">
                          <button
                            class="guest-tables__seat-count-button"
                            @click="selectedTableWidth = Math.max(2, selectedTableWidth - 1)"
                          >
                            <i class="pi pi-minus"></i>
                          </button>
                          <span class="guest-tables__seat-count-value">{{ selectedTableWidth }}</span>
                          <button
                            class="guest-tables__seat-count-button"
                            @click="selectedTableWidth = Math.min(100, selectedTableWidth + 1)"
                          >
                            <i class="pi pi-plus"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  </template>
                </div>
                <div class="guest-tables__dropdown-actions">
                  <button
                    class="guest-tables__button guest-tables__button--success"
                    @click="addTable"
                  >
                    <i class="pi pi-check"></i>
                    Dodaj
                  </button>
                  <button
                    class="guest-tables__button guest-tables__button--danger"
                    @click="showShapeDropdown = false"
                  >
                    <i class="pi pi-times"></i>
                    Anuluj
                  </button>
                </div>
              </div>
            </transition>
          </div>
          <button
            v-if="selectedTable"
            class="guest-tables__button guest-tables__button--danger"
            @click="deleteTable(tables.indexOf(selectedTable))"
          >
            <i class="pi pi-trash"></i>
            <span class="guest-tables__button-text">Usuń stół</span>
          </button>
          <button
            class="guest-tables__button guest-tables__button--success"
            @click="onTablesSave"
          >
            <i class="pi pi-save"></i>
            <span class="guest-tables__button-text">Zapisz</span>
          </button>
        </div>
      </div>

      <div class="guest-tables__container">
        <transition name="slide">
          <div
            v-if="selectedGuest"
            class="guest-tables__details"
          >
            <div class="guest-tables__details-header guest-tables__details-header--guest">
              <div class="guest-tables__details-header-top">
                <h3 class="guest-tables__details-title">Szczegóły gościa</h3>
                <button
                  class="guest-tables__details-close"
                  @click="onCancelAdding"
                >
                  <i class="pi pi-times"></i>
                </button>
              </div>
            </div>
            <div class="guest-tables__details-content">
              <div class="guest-tables__details-row">
                <span class="guest-tables__details-label">Imię:</span>
                <span class="guest-tables__details-value">{{ selectedGuest.name }}</span>
              </div>
              <div class="guest-tables__details-row">
                <span class="guest-tables__details-label">Nazwisko:</span>
                <span class="guest-tables__details-value">{{ selectedGuest.surname }}</span>
              </div>
              <div class="guest-tables__details-row">
                <span class="guest-tables__details-label">Obecność:</span>
                <span class="guest-tables__details-value">{{ selectedGuest.presence ? 'Tak' : 'Nie' }}</span>
              </div>
              <div class="guest-tables__details-row">
                <span class="guest-tables__details-label">Numer stołu:</span>
                <span class="guest-tables__details-value">{{
                  selectedGuest.tableNumber === -1 ? 'Brak' : selectedGuest.tableNumber
                }}</span>
              </div>
              <div class="guest-tables__details-row">
                <span class="guest-tables__details-label">Informacje tekstowe:</span>
                <div class="guest-tables__text-info">
                  <template v-if="!isEditingTextInfo">
                    <div class="guest-tables__text-display">
                      <span class="guest-tables__details-value">{{ currentTextInfo }}</span>
                      <button
                        class="guest-tables__edit-button"
                        @click="onEditTextInfo"
                      >
                        <i class="pi pi-pencil"></i>
                      </button>
                    </div>
                  </template>
                  <template v-else>
                    <textarea
                      v-model="editedTextInfo"
                      class="guest-tables__text-input"
                      rows="3"
                      placeholder="Wprowadź informacje tekstowe..."
                    ></textarea>
                    <div class="guest-tables__text-actions">
                      <button
                        class="guest-tables__button guest-tables__button--success"
                        @click="onSaveTextInfo"
                      >
                        <i class="pi pi-check"></i>
                      </button>
                      <button
                        class="guest-tables__button guest-tables__button--danger"
                        @click="onCancelTextInfoEdit"
                      >
                        <i class="pi pi-times"></i>
                      </button>
                    </div>
                  </template>
                </div>
              </div>
              <div class="guest-tables__details-row">
                <button
                  v-if="selectedGuest?.tableNumber && selectedGuest.tableNumber > 0"
                  class="guest-tables__button guest-tables__button--danger"
                  @click="removeFromTable"
                >
                  <i class="pi pi-user-minus"></i>
                  Usuń ze stołu
                </button>
              </div>
            </div>
          </div>
        </transition>

        <transition name="slide">
          <div
            v-if="selectedTable"
            class="guest-tables__details"
          >
            <div class="guest-tables__details-header">
              <div class="guest-tables__details-header-top">
                <h3 class="guest-tables__details-title">Szczegóły stołu</h3>
                <button
                  class="guest-tables__details-close"
                  @click="onCancelTableEdit"
                >
                  <i class="pi pi-times"></i>
                </button>
              </div>
              <div class="guest-tables__details-tabs">
                <button
                  class="guest-tables__tab-button"
                  :class="{ 'guest-tables__tab-button--active': activeTableTab === TableTab.DETAILS }"
                  @click="switchTableTab(TableTab.DETAILS)"
                >
                  Szczegóły
                </button>
                <button
                  v-if="guests.length > 0"
                  class="guest-tables__tab-button"
                  :class="{ 'guest-tables__tab-button--active': activeTableTab === TableTab.GUESTS }"
                  @click="switchTableTab(TableTab.GUESTS)"
                >
                  Nieprzypisani goście
                </button>
              </div>
            </div>
            <div class="guest-tables__details-content">
              <template v-if="activeTableTab === TableTab.DETAILS">
                <div class="guest-tables__details-row">
                  <span class="guest-tables__details-label">Kształt:</span>
                  <span class="guest-tables__details-value">{{
                    selectedTable.shape === TableShape.ROUND ? 'Okrągły' : 'Prostokątny'
                  }}</span>
                </div>
                <div class="guest-tables__details-row">
                  <span class="guest-tables__details-label">Liczba gości:</span>
                  <span class="guest-tables__details-value">{{
                    tableGuests[tables.indexOf(selectedTable)]?.length ?? 0
                  }}</span>
                </div>
                <div class="guest-tables__details-row">
                  <span class="guest-tables__details-label">Etykieta:</span>
                  <div class="guest-tables__text-info">
                    <template v-if="!isEditingTableLabel">
                      <div class="guest-tables__text-display">
                        <span class="guest-tables__details-value">{{ selectedTable.label || 'Brak' }}</span>
                        <button
                          class="guest-tables__edit-button"
                          @click="onEditTableLabel"
                        >
                          <i class="pi pi-pencil"></i>
                        </button>
                      </div>
                    </template>
                    <template v-else>
                      <textarea
                        v-model="editedTableLabel"
                        class="guest-tables__text-input"
                        rows="2"
                        placeholder="Wprowadź etykietę stołu..."
                      ></textarea>
                      <div class="guest-tables__text-actions">
                        <button
                          class="guest-tables__button guest-tables__button--success"
                          @click="onSaveTableLabel"
                        >
                          <i class="pi pi-check"></i>
                        </button>
                        <button
                          class="guest-tables__button guest-tables__button--danger"
                          @click="onCancelTableLabelEdit"
                        >
                          <i class="pi pi-times"></i>
                        </button>
                      </div>
                    </template>
                  </div>
                </div>
                <div class="guest-tables__details-row">
                  <span class="guest-tables__details-label">Liczba miejsc:</span>
                  <template v-if="selectedTable.shape === TableShape.ROUND">
                    <div class="guest-tables__seat-count">
                      <button
                        class="guest-tables__seat-count-button"
                        @click="
                          handleSeatCountChange(
                            tables.indexOf(selectedTable),
                            Math.max(2, (selectedTable?.seatCount ?? 0) - 1),
                          )
                        "
                      >
                        <i class="pi pi-minus"></i>
                      </button>
                      <span class="guest-tables__seat-count-value">{{ selectedTable.seatCount }}</span>
                      <button
                        class="guest-tables__seat-count-button"
                        @click="
                          handleSeatCountChange(
                            tables.indexOf(selectedTable),
                            Math.min(100, (selectedTable?.seatCount ?? 0) + 1),
                          )
                        "
                      >
                        <i class="pi pi-plus"></i>
                      </button>
                    </div>
                  </template>
                  <template v-else>
                    <div class="guest-tables__dimensions">
                      <div class="guest-tables__dimension">
                        <span class="guest-tables__dimension-label">Długość:</span>
                        <div class="guest-tables__seat-count">
                          <button
                            class="guest-tables__seat-count-button"
                            @click="
                              handleSeatCountChange(
                                tables.indexOf(selectedTable),
                                (selectedTable.length || 4) * (selectedTable.width || 2),
                                Math.max(2, (selectedTable.length || 4) - 1),
                                selectedTable.width || 2,
                              )
                            "
                          >
                            <i class="pi pi-minus"></i>
                          </button>
                          <span class="guest-tables__seat-count-value">{{ selectedTable.length || 4 }}</span>
                          <button
                            class="guest-tables__seat-count-button"
                            @click="
                              handleSeatCountChange(
                                tables.indexOf(selectedTable),
                                (selectedTable.length || 4) * (selectedTable.width || 2),
                                Math.min(100, (selectedTable.length || 4) + 1),
                                selectedTable.width || 2,
                              )
                            "
                          >
                            <i class="pi pi-plus"></i>
                          </button>
                        </div>
                      </div>
                      <div class="guest-tables__dimension">
                        <span class="guest-tables__dimension-label">Szerokość:</span>
                        <div class="guest-tables__seat-count">
                          <button
                            class="guest-tables__seat-count-button"
                            @click="
                              handleSeatCountChange(
                                tables.indexOf(selectedTable),
                                (selectedTable.length || 4) * (selectedTable.width || 2),
                                selectedTable.length || 4,
                                Math.max(2, (selectedTable.width || 2) - 1),
                              )
                            "
                          >
                            <i class="pi pi-minus"></i>
                          </button>
                          <span class="guest-tables__seat-count-value">{{ selectedTable.width || 2 }}</span>
                          <button
                            class="guest-tables__seat-count-button"
                            @click="
                              handleSeatCountChange(
                                tables.indexOf(selectedTable),
                                (selectedTable.length || 4) * (selectedTable.width || 2),
                                selectedTable.length || 4,
                                Math.min(100, (selectedTable.width || 2) + 1),
                              )
                            "
                          >
                            <i class="pi pi-plus"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  </template>
                </div>
                <div class="guest-tables__details-row">
                  <span class="guest-tables__details-label">Goście przy stole:</span>
                  <div class="guest-tables__table-guests">
                    <div
                      v-if="!tableGuests[tables.indexOf(selectedTable)]?.length"
                      class="guest-tables__no-guests"
                    >
                      Brak gości
                    </div>
                    <div
                      v-else
                      class="guest-tables__guests-list"
                    >
                      <div
                        v-for="guest in tableGuests[tables.indexOf(selectedTable)] || []"
                        :key="guest.id"
                        class="guest-tables__guest-item"
                        @click="onGuestSelected(guest)"
                      >
                        <span class="guest-tables__guest-name">{{ guest.name }} {{ guest.surname }}</span>
                        <button class="guest-tables__guest-edit">
                          <i class="pi pi-pencil"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="guest-tables__details-row">
                  <button
                    class="guest-tables__button guest-tables__button--danger"
                    @click="deleteTable(tables.indexOf(selectedTable))"
                  >
                    <i class="pi pi-trash"></i>
                    Usuń stół
                  </button>
                </div>
              </template>
              <template v-else>
                <div class="guest-tables__unassigned-guests">
                  <div
                    v-if="guests.length > 0"
                    class="guest-tables__search"
                  >
                    <div class="guest-tables__search-container">
                      <input
                        v-model="unassignedGuestsSearchQuery"
                        type="text"
                        placeholder="Szukaj gości..."
                        class="guest-tables__search-input"
                      />
                      <i class="pi pi-search guest-tables__search-icon"></i>
                    </div>
                  </div>
                  <div class="guest-tables__table-container">
                    <div
                      v-if="guests.length === 0"
                      class="guest-tables__no-unassigned-guests"
                    >
                      <i class="pi pi-info-circle"></i>
                      <p>Wybierz gościa z innego stołu, aby przenieść go na to miejsce</p>
                    </div>
                    <div
                      v-else-if="filteredUnassignedGuests.length === 0"
                      class="guest-tables__no-unassigned-guests"
                    >
                      <i class="pi pi-search"></i>
                      <p>Nie znaleziono gości</p>
                    </div>
                    <table
                      v-else
                      class="guest-tables__table"
                    >
                      <thead class="guest-tables__table-head">
                        <tr>
                          <th
                            v-for="col in columns"
                            :key="col.field"
                            class="guest-tables__table-cell"
                          >
                            {{ col.header }}
                          </th>
                        </tr>
                      </thead>
                      <tbody class="guest-tables__table-body">
                        <tr
                          v-for="guest in filteredUnassignedGuests"
                          :key="guest.id"
                          class="guest-tables__table-row"
                          :class="{ 'guest-tables__table-row--selected': isGuestSelected(guest) }"
                          @click="onGuestSelected(guest)"
                        >
                          <td
                            v-for="col in columns"
                            :key="col.field"
                            class="guest-tables__table-cell"
                          >
                            {{ guest[col.field] }}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </template>
            </div>
          </div>
        </transition>

        <div class="guest-tables__canvas">
          <div
            v-if="tables.length === 0"
            class="guest-tables__empty-state"
          >
            <div class="guest-tables__empty-state-content">
              <div class="guest-tables__empty-state-icon">
                <i class="pi pi-table"></i>
              </div>
              <h2 class="guest-tables__empty-state-title">Rozpocznij układanie stołów</h2>
              <p class="guest-tables__empty-state-description">
                Kliknij przycisk "Dodaj stół" aby rozpocząć tworzenie układu stołów dla Twoich gości.
              </p>
              <button
                class="guest-tables__button guest-tables__button--primary guest-tables__empty-state-button"
                @click="showShapeDropdown = true"
              >
                <i class="pi pi-plus"></i>
                Dodaj pierwszy stół
              </button>
            </div>
          </div>
          <TableCanvas
            v-else
            :tables="
              tables.map((table, index) => ({
                ...table,
                guests: tableGuests[index] || [],
              }))
            "
            :selected-guest-id="selectedGuest?.id || null"
            :selected-table="
              selectedTable
                ? ({
                    ...selectedTable,
                    guests: tableGuests[tables.indexOf(selectedTable)] || [],
                  } as TableWithGuests)
                : null
            "
            @guest-click="onGuestSelected"
            @table-click="onTableSelected"
            @table-position-change="handleTablePositionChange"
            @add-guest="addGuestToTable"
            @seat-count-change="handleSeatCountChange"
            @canvas-resize="handleCanvasResize"
            @drag-start="isDragging = true"
            @drag-end="isDragging = false"
            @show-unassigned-guests="handleShowUnassignedGuests"
          />
        </div>
      </div>
    </template>
    <Toast position="bottom-right" />
  </div>
</template>

<style lang="scss" scoped>
/* stylelint-disable no-descending-specificity */
@use 'sass:color';

.guest-tables {
  padding: 2rem;
  min-height: 30rem;
  width: 100%;

  i {
    font-size: 0.875rem;
  }

  &__details-value {
    text-align: right;
    flex: 1;
    color: #000;
  }

  &__header {
    display: flex;
    justify-content: space-between;
    margin-bottom: 2rem;
    flex-wrap: wrap;
    gap: 1rem;
  }

  &__navigation {
    display: flex;
    gap: 1rem;
  }

  &__actions {
    display: flex;
    gap: 1rem;
    align-items: center;
    flex-wrap: wrap;
  }

  &__shape-dropdown {
    position: relative;
  }

  &__dropdown-menu {
    position: absolute;
    top: 100%;
    right: 0;
    background-color: $white;
    border: 1px solid $brown-300;
    border-radius: 4px;
    box-shadow: 0 2px 4px rgba($black, 0.1);
    z-index: 100;
    margin-top: 0.5rem;
    min-width: 250px;
    padding: 1rem;

    @media (max-width: 480px) {
      position: fixed;
      inset: auto 0 0;
      width: 100%;
      margin-top: 0;
      border-radius: 8px 8px 0 0;
      transform: translateY(100%);
      animation: slideUp 0.3s ease forwards;
    }
  }

  &__dropdown-section {
    margin-bottom: 1rem;
  }

  &__dropdown-title {
    font-size: 1.5rem;
    color: $brown-200;
    margin-bottom: 0.5rem;
    font-weight: 600;
  }

  &__dropdown-options {
    display: flex;
    gap: 0.5rem;
  }

  &__dropdown-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 1.25rem;
    width: 100%;
    border: 2px solid $brown-300;
    background: $white;
    cursor: pointer;
    color: $brown-200;
    font-weight: 600;
    font-size: 1.1rem;
    transition: all 0.3s ease;
    border-radius: 6px;

    i {
      font-size: 1.25rem;
    }

    &:hover {
      background-color: $brown-600;
      border-color: $brown-200;
      color: $brown-200;
    }

    &--selected {
      background-color: $brown-200;
      color: $white;
      border-color: $brown-200;

      &:hover {
        background-color: $brown-200;
        color: $white;
      }
    }
  }

  &__seat-count {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    padding: 0.75rem;
    background-color: $white;
    border: 2px solid $brown-300;
    border-radius: 6px;
  }

  &__seat-count-button {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    border: 2px solid $brown-200;
    background-color: $white;
    color: $brown-200;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.3s ease;
    font-size: 1.1rem;

    &:hover {
      background-color: $brown-200;
      color: $white;
    }
  }

  &__seat-count-value {
    font-size: 1.4rem;
    font-weight: bold;
    color: $brown-200;
    min-width: 2.5rem;
    text-align: center;
  }

  &__dropdown-actions {
    display: flex;
    gap: 0.5rem;
    margin-top: 1rem;
    padding-top: 1rem;
    border-top: 1px solid $brown-300;
  }

  &__button {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 1rem;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.3s ease;
    font-weight: 600;
    white-space: nowrap;

    i {
      font-size: 1rem;
    }

    &--primary {
      background-color: $brown-200;
      color: $white;

      &:hover {
        background-color: $brown-100;
      }
    }

    &--success {
      background-color: $status-green;
      color: $white;

      &:hover {
        background-color: color.adjust($status-green, $lightness: -10%);
      }
    }

    &--danger {
      background-color: $status-red;
      color: $white;

      &:hover {
        background-color: color.adjust($status-red, $lightness: -10%);
      }
    }

    &--info {
      background-color: $brown-200;
      color: $white;

      &:hover {
        background-color: $brown-500;
      }
    }
  }

  &__link {
    text-decoration: none;
  }

  &__container {
    display: flex;
    gap: 2rem;
    margin-top: 2rem;
  }

  &__unassigned {
    flex: 0 0 300px;
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  &__canvas {
    flex: 1;
    min-height: 600px;
  }

  &__table-header {
    display: flex;
    align-items: center;
    gap: 1rem;
    flex-wrap: wrap;
  }

  &__table-title {
    font-size: 1.5rem;
    color: $black-300;
    margin: 0;
  }

  &__table-container {
    overflow-x: auto;
  }

  &__table {
    width: 100%;
    border-collapse: collapse;
    background-color: $white;
    box-shadow: 0 2px 4px rgba($black, 0.1);
  }

  &__table-head {
    background-color: $brown-600;
  }

  &__table-cell {
    padding: 1rem;
    text-align: left;
    font-weight: bold;
    color: $black-300;
    border-bottom: 2px solid $brown-300;
  }

  &__table-body {
    tr:nth-child(even) {
      background-color: $white-200;
    }
  }

  &__table-row {
    cursor: pointer;
    transition: background-color 0.3s ease;

    &:hover {
      background-color: $brown-450;
    }

    &--selected {
      background-color: $brown-450;
    }
  }

  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.3s ease;
  }

  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
  }

  .guest-tables__details {
    position: absolute;
    left: 0;
    top: 57px;
    width: 300px;
    height: calc(100% - 57px);
    background-color: white;
    border-right: 1px solid #834920;
    z-index: 10;
    display: flex;
    flex-direction: column;
    box-shadow: 2px 0 4px rgb(0 0 0 / 10%);
    overflow-y: auto;

    &-header {
      padding: 1.2rem 1rem 0.25rem;
      border-bottom: 1px solid #834920;
      display: flex;
      flex-direction: column;
      gap: 1rem;

      &--guest {
        padding: 1.2rem 1rem;
      }
    }

    &-header-top {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    &-title {
      font-size: 2.5rem;
      color: #834920;
      margin: 0;
    }

    &-close {
      background: none;
      border: none;
      color: #834920;
      cursor: pointer;
      padding: 0.5rem;
      border-radius: 50%;
      transition: all 0.3s ease;

      &:hover {
        background-color: #f2e9e4;
      }

      i {
        font-size: 1.5rem;
      }
    }

    &-content {
      padding: 1rem;
      flex: 1;
    }

    &-row {
      display: flex;
      gap: 1rem;
      justify-content: space-between;
      margin-bottom: 1rem;
      padding-bottom: 0.5rem;
      border-bottom: 1px solid #f2e9e4;

      &:last-child {
        border-bottom: none;
        margin-bottom: 0;
        padding-bottom: 0;
      }
    }

    &-label {
      font-weight: bold;
      color: #834920;
    }

    &-value {
      text-align: right;
      flex: 1;
      color: #000;
    }
  }

  .slide-enter-active,
  .slide-leave-active {
    transition: transform 0.3s ease;
  }

  .slide-enter-from,
  .slide-leave-to {
    transform: translateX(-100%);
  }

  .slide-enter-to,
  .slide-leave-from {
    transform: translateX(0);
  }

  @media (max-width: 768px) {
    padding: 0.5rem;

    &__header {
      flex-direction: column;
    }

    &__navigation,
    &__actions {
      flex-direction: column;
    }

    &__container {
      flex-direction: column;
    }

    &__unassigned {
      flex: none;
      width: 100%;
    }

    &__canvas {
      width: 100%;
      min-height: 400px;
    }
  }

  &__text-info {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    flex: 1;
  }

  &__text-display {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 0.5rem;
    min-height: 2rem;
    padding: 0.25rem;
    border-radius: 4px;
    width: 100%;
  }

  &__edit-button {
    background: none;
    border: none;
    cursor: pointer;
    padding: 3px;
    border-radius: 4px;
    opacity: 1;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    color: $brown-200;

    &:hover {
      background-color: $brown-100;
      color: $white;
    }
  }

  &__text-input {
    width: 100%;
    padding: 0.5rem;
    border: 1px solid $brown-300;
    border-radius: 4px;
    resize: vertical;
    font-family: inherit;
    font-size: 1rem;
    background-color: $white;
    color: $black-300;
    margin-bottom: 0.5rem;

    &:focus {
      outline: none;
      border-color: $brown-400;
      box-shadow: 0 0 0 2px rgba($brown-400, 0.2);
    }
  }

  &__text-actions {
    display: flex;
    gap: 0.5rem;
    justify-content: flex-end;
  }

  &__table-guests {
    flex: 1;
  }

  &__no-guests {
    color: $brown-400;
    font-style: italic;
  }

  &__guests-list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  &__guest-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.5rem;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.3s ease;
    color: $brown-200;

    &:hover {
      background-color: $brown-600;

      .guest-tables__guest-edit {
        opacity: 1;
      }
    }
  }

  &__guest-name {
    flex: 1;
  }

  &__guest-edit {
    background: none;
    border: none;
    color: inherit;
    cursor: pointer;
    padding: 0.25rem;
    border-radius: 4px;
    opacity: 0;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      background-color: rgba($white, 0.1);
    }
  }

  &__details-tabs {
    display: flex;
    gap: 0;
    margin: 0.5rem 0;
    border-bottom: none;
  }

  &__tab-button {
    padding: 0.75rem 1.5rem;
    border: none;
    background: none;
    cursor: pointer;
    color: $brown-400;
    font-weight: 600;
    transition: all 0.3s ease;
    position: relative;
    margin-bottom: 0;
    border-bottom: 2px solid transparent;

    &:hover {
      color: $brown-200;
    }

    &--active {
      color: $brown-200;
      border-bottom: 2px solid $brown-200;
    }
  }

  &__unassigned-guests {
    flex: 1;
    overflow-y: auto;
    max-height: calc(100% - 200px);
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  &__search {
    flex-shrink: 0;
  }

  &__search-container {
    display: flex;
    align-items: center;
    background-color: #f2e9e4;
    border: 1px solid $brown-300;
    border-radius: 4px;
    overflow: hidden;
    height: 40px;
    padding: 0 0.75rem;
    gap: 0.5rem;
  }

  &__search-input {
    flex: 1;
    border: none;
    font-size: 1rem;
    background: transparent;
    color: #834920;
    min-width: 0;

    &::placeholder {
      color: #834920;
      opacity: 1;
      font-weight: 600;
    }

    &:focus {
      outline: none;
    }
  }

  &__search-icon {
    font-size: 1.25rem;
    color: #834920;
    flex-shrink: 0;
  }

  &__dimensions {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    width: 100%;
  }

  &__dimension {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
  }

  &__dimension-label {
    font-weight: 600;
    color: $brown-200;
    min-width: 80px;
  }

  &__empty-state {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    min-height: 600px;
    background-color: $white;
    border: 2px dashed $brown-300;
    border-radius: 8px;
    animation: pulse 2s infinite;

    @media (max-width: 480px) {
      min-height: 300px;
    }
  }

  &__empty-state-content {
    text-align: center;
    padding: 2rem;
    max-width: 400px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }

  &__empty-state-icon {
    font-size: 4rem;
    color: $brown-300;
    margin-bottom: 1.5rem;
    animation: bounce 2s infinite;

    i {
      font-size: 4rem;
    }
  }

  &__empty-state-title {
    font-size: 1.8rem;
    color: $brown-200;
    margin-bottom: 1rem;
  }

  &__empty-state-description {
    color: $brown-400;
    margin-bottom: 2rem;
    line-height: 1.5;
  }

  &__empty-state-button {
    padding: 0.75rem 1.5rem;
    font-size: 1.5rem;
  }

  @keyframes pulse {
    0% {
      border-color: $brown-300;
    }

    50% {
      border-color: $brown-200;
    }

    100% {
      border-color: $brown-300;
    }
  }

  @keyframes bounce {
    0%,
    100% {
      transform: translateY(0);
    }

    50% {
      transform: translateY(-10px);
    }
  }

  &__no-unassigned-guests {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 2rem;
    text-align: center;
    gap: 1rem;
    color: $brown-200;

    i {
      font-size: 3rem;
      color: $brown-200;
    }

    p {
      margin: 0;
      font-size: 1.5rem;
      line-height: 1.5;
    }
  }
}
</style>
